import type { WidgetConfig, ContentType, WidgetMode, ButtonType, ButtonSize, Environment } from '@/types/config';
import { DEFAULT_CONFIG } from '@/types/config';
import { Sanitizer } from '@/security/Sanitizer';

/**
 * Parses widget configuration from button data attributes
 */
export class ConfigParser {
  /**
   * Parse widget configuration from button element
   * @param button - Button element with cd--* attributes
   * @returns Parsed widget configuration
   * @throws Error if required attributes are missing or invalid
   */
  static parse(button: HTMLButtonElement): WidgetConfig {
    const contentIdRaw = button.getAttribute('cd--content-id');
    if (!contentIdRaw) {
      throw new Error('[CaredoveWidget] cd--content-id attribute is required');
    }

    const { contentType, contentId } = this.parseContentId(contentIdRaw);

    return {
      contentType,
      contentId: Sanitizer.sanitizeContentId(contentId),
      mode: this.parseMode(button),
      subdomain: this.parseSubdomain(button),
      parameters: button.getAttribute('cd--parameters') || undefined,
      width: button.getAttribute('cd--width') || DEFAULT_CONFIG.width,
      height: button.getAttribute('cd--height') || undefined,
      variableWidth: button.getAttribute('cd--variable-width') !== 'false',
      variableHeight: button.getAttribute('cd--variable-height') !== 'false',
      title: button.getAttribute('cd--title') || undefined,
      elementId: button.getAttribute('cd--element-id') || undefined,
      locked: button.getAttribute('cd--locked') === 'true',
      showLink: button.getAttribute('cd--show-link') === 'true',
      minHeight: this.parseNumber(button.getAttribute('cd--min-height'), DEFAULT_CONFIG.minHeight!),
      borderSize: this.parseNumber(button.getAttribute('cd--border-size'), DEFAULT_CONFIG.borderSize!),
      borderColor: this.parseColor(button.getAttribute('cd--border-color')) || DEFAULT_CONFIG.borderColor!,
      borderRadius: button.getAttribute('cd--border-radius') || DEFAULT_CONFIG.borderRadius!,
      buttonType: this.parseButtonType(button.getAttribute('cd--button-type')),
      buttonSize: this.parseButtonSize(button.getAttribute('cd--button-size')),
      buttonColor: this.parseColor(button.getAttribute('cd--button-color')),
      buttonHoverColor: this.parseColor(button.getAttribute('cd--button-hover-color')),
      buttonTextColor: this.parseColor(button.getAttribute('cd--button-text-color')),
      buttonTextHoverColor: this.parseColor(button.getAttribute('cd--button-text-hover-color')),
      buttonBorderColor: this.parseColor(button.getAttribute('cd--button-border-color')),
      buttonBorderHoverColor: this.parseColor(button.getAttribute('cd--button-border-hover-color')),
    };
  }

  /**
   * Parse content ID into type and ID
   * Format: "cd-searchsite:mealsonwheels" or "https://example.com"
   */
  private static parseContentId(raw: string): { contentType: ContentType; contentId: string } {
    // Check for cd-* format first (has precedence over URL)
    if (raw.startsWith('cd-') || raw.startsWith('url-element:')) {
      const colonIndex = raw.indexOf(':');
      if (colonIndex === -1) {
        throw new Error(`[CaredoveWidget] Invalid content ID format: ${raw}`);
      }

      const type = raw.substring(0, colonIndex);
      const id = raw.substring(colonIndex + 1);

      // Validate content type
      const validTypes: ContentType[] = [
        'cd-searchsite',
        'cd-form',
        'cd-routing-form',
        'cd-service',
        'cd-request',
        'url',
        'url-element',
      ];

      if (!validTypes.includes(type as ContentType)) {
        throw new Error(`[CaredoveWidget] Invalid content type: ${type}`);
      }

      return {
        contentType: type as ContentType,
        contentId: id,
      };
    }

    // URL detection (for custom URLs)
    if (this.isValidUrl(raw)) {
      return { contentType: 'url', contentId: raw };
    }

    throw new Error(`[CaredoveWidget] Invalid content ID format: ${raw}`);
  }

  /**
   * Check if string is a valid URL
   */
  private static isValidUrl(str: string): boolean {
    try {
      new URL(str);
      return true;
    } catch {
      return str.startsWith('http://') || str.startsWith('https://');
    }
  }

  /**
   * Parse and validate mode
   */
  private static parseMode(button: HTMLButtonElement): WidgetMode {
    const mode = button.getAttribute('cd--mode') || 'overlay';
    const validModes: WidgetMode[] = ['overlay', 'inline', 'inline-simple', 'redirect', 'new-tab'];

    if (!validModes.includes(mode as WidgetMode)) {
      console.warn(`[CaredoveWidget] Invalid mode "${mode}", defaulting to "overlay"`);
      return 'overlay';
    }

    return mode as WidgetMode;
  }

  /**
   * Parse subdomain/environment
   */
  private static parseSubdomain(button: HTMLButtonElement): Environment | undefined {
    const subdomain = button.getAttribute('cd--subdomain');
    if (!subdomain) return undefined;

    const validSubdomains: Environment[] = ['prod', 'dev', 'stage'];
    if (!validSubdomains.includes(subdomain as Environment)) {
      console.warn(`[CaredoveWidget] Invalid subdomain "${subdomain}"`);
      return undefined;
    }

    return subdomain as Environment;
  }

  /**
   * Parse and validate color
   */
  private static parseColor(color: string | null): string | undefined {
    if (!color) return undefined;

    const sanitized = Sanitizer.sanitizeCss(color);

    // Validate hex color
    if (sanitized.startsWith('#')) {
      if (Sanitizer.isValidHexColor(sanitized)) {
        return sanitized;
      }
      console.warn(`[CaredoveWidget] Invalid hex color: ${color}`);
      return undefined;
    }

    // Allow CSS color names and rgb/rgba values
    if (/^(rgb|rgba|hsl|hsla)\(/.test(sanitized) || /^[a-z]+$/i.test(sanitized)) {
      return sanitized;
    }

    return undefined;
  }

  /**
   * Parse number attribute with default
   */
  private static parseNumber(value: string | null, defaultValue: number): number {
    if (!value) return defaultValue;

    const parsed = parseInt(value, 10);
    if (isNaN(parsed)) {
      console.warn(`[CaredoveWidget] Invalid number: ${value}, using default: ${defaultValue}`);
      return defaultValue;
    }

    return parsed;
  }

  /**
   * Parse button type
   */
  private static parseButtonType(type: string | null): ButtonType | undefined {
    if (!type) return undefined;

    const validTypes: ButtonType[] = ['primary', 'secondary', 'link'];
    if (!validTypes.includes(type as ButtonType)) {
      console.warn(`[CaredoveWidget] Invalid button type: ${type}`);
      return undefined;
    }

    return type as ButtonType;
  }

  /**
   * Parse button size
   */
  private static parseButtonSize(size: string | null): ButtonSize | undefined {
    if (!size) return undefined;

    // Map 'middle' to 'medium' for backwards compatibility
    if (size === 'middle') return 'medium';

    const validSizes: ButtonSize[] = ['small', 'medium', 'large'];
    if (!validSizes.includes(size as ButtonSize)) {
      console.warn(`[CaredoveWidget] Invalid button size: ${size}`);
      return undefined;
    }

    return size as ButtonSize;
  }
}
